import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

/** Mirrors `KirbyMode` in the scheduler and `@c360/backend-services`. */
export type KirbyMode = 'full' | 'ses-only' | 'off';

/**
 * Which Kirby services this environment runs.
 *
 * An environment fact, not a per-user or per-game one — it comes from
 * KIRBY_MODE, which only changes on a redeploy — so it is fetched once and kept
 * forever rather than polled. Three places read it: the Resources page header,
 * the card (which hides the gateway and TM controls under `ses-only`) and the
 * navigation menu (which disables the entry under `off`).
 *
 * Defaults to `off` while in flight, matching how both the scheduler and the
 * endpoint resolve an absent value: the failure that hides a control is far
 * cheaper than the one that offers a launch nothing will pick up.
 */
export function useKirbyMode(): { mode: KirbyMode; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['kirbyMode'],
    queryFn: async () => {
      const { data } = await get<{ mode: KirbyMode }>('/v1.0/kirby/mode');
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });

  return { mode: data?.mode ?? 'off', isLoading };
}
